import { useEffect, useRef, useState } from "react"

import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ANY } from "@/lib/filters"
import { useApi } from "@/lib/hooks"
import { dash } from "@/lib/utils"
import {
  labelOf,
  NY_COURT_FAMILIES,
  NY_COURT_LABELS,
  nyCaseYears,
  POSTURES,
  QUESTION_KINDS,
  SUBJECT_AREAS,
} from "@shared/ny"
import type { NyCase } from "@shared/types"

const YEARS = nyCaseYears()

export const caseLine = (c: NyCase) =>
  [c.citation, NY_COURT_LABELS[c.courtFamily] ?? c.courtFamily, c.decidedDate]
    .filter(Boolean)
    .join(" · ")

function Filter({
  value,
  onChange,
  anyLabel,
  options,
  className,
}: {
  value: string
  onChange: (v: string) => void
  anyLabel: string
  options: readonly string[] | readonly { value: string; label: string }[]
  className?: string
}) {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: labelOf(o) } : o))
  return (
    <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)}>
      <SelectTrigger
        className={`${className ?? "w-40"} overflow-hidden [&_[data-slot=select-value]]:truncate`}
      >
        <SelectValue placeholder={anyLabel} />
      </SelectTrigger>
      <SelectContent className="w-auto">
        <SelectItem value={ANY}>{anyLabel}</SelectItem>
        {opts.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export function CasePicker({ onPick }: { onPick: (c: NyCase) => void }) {
  const [text, setText] = useState("")
  const [q, setQ] = useState("")
  const [court, setCourt] = useState("")
  const [year, setYear] = useState("")
  const [question, setQuestion] = useState("")
  const [subject, setSubject] = useState("")
  const [posture, setPosture] = useState("")
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const id = setTimeout(() => setQ(text.trim()), 250)
    return () => clearTimeout(id)
  }, [text])

  const params = new URLSearchParams()
  if (q) params.set("q", q)
  if (court) params.set("court", court)
  if (year) params.set("year", year)
  if (question) params.set("questionKind", question)
  if (subject) params.set("subject", subject)
  if (posture) params.set("posture", posture)
  const url = `/api/ny-cases?${params}`

  const { data, err } = useApi<NyCase[]>(() => fetch(url), [url])
  const cases = data ?? []

  // Fresh results mean the highlighted row is meaningless.
  useEffect(() => setActive(0), [url])

  // Click-away closes the list without clearing what was typed.
  useEffect(() => {
    const away = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    addEventListener("mousedown", away)
    return () => removeEventListener("mousedown", away)
  }, [])

  const pick = (c: NyCase | undefined) => {
    if (!c) return
    setOpen(false)
    setText("")
    onPick(c)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") return setOpen(false)
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      setOpen(true)
      setActive((i) => {
        const n = cases.length
        return n ? (i + (e.key === "ArrowDown" ? 1 : n - 1)) % n : 0
      })
    } else if (e.key === "Enter") {
      e.preventDefault()
      pick(cases[active])
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div ref={box} className="relative w-64">
        <Input
          value={text}
          placeholder="Pick case…"
          onChange={(e) => {
            setText(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />

        {/* Wider than the input it hangs off — summaries need the room. */}
        {open && (
          <div className="absolute z-50 mt-1 max-h-96 w-[40rem] max-w-[calc(100vw-3rem)] overflow-y-auto border bg-popover text-popover-foreground">
            {err ? (
              <p className="px-3 py-2 text-sm text-red-600">{err}</p>
            ) : !data ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">Loading…</p>
            ) : !cases.length ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">No matching cases.</p>
            ) : (
              cases.map((c, i) => (
                <button
                  key={c.id}
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(c)}
                  className={`block w-full space-y-0.5 border-b px-3 py-2 text-left last:border-b-0 ${
                    i === active ? "bg-accent text-accent-foreground" : ""
                  }`}
                >
                  <span className="block truncate font-medium">{dash(c.name)}</span>
                  {c.summary && <span className="block text-xs">{c.summary}</span>}
                  <span className="block truncate text-xs text-muted-foreground">
                    {caseLine(c)}
                  </span>
                  {/* Primary only, to match what the filters select on. */}
                  {Boolean(c.questionKinds?.[0] || c.postures?.[0]) && (
                    <span className="block truncate text-xs text-muted-foreground">
                      {[c.questionKinds?.[0], c.postures?.[0]]
                        .filter(Boolean)
                        .map((v) => labelOf(v as string))
                        .join(" · ")}
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Question kind leads: it is also what the results are ordered by. */}
      <Filter value={question} onChange={setQuestion} anyLabel="Question" options={QUESTION_KINDS} className="w-44" />
      <Filter value={subject} onChange={setSubject} anyLabel="Subject" options={SUBJECT_AREAS} />
      <Filter value={posture} onChange={setPosture} anyLabel="Posture" options={POSTURES} />
      <Filter
        value={court}
        onChange={setCourt}
        anyLabel="All courts"
        options={NY_COURT_FAMILIES}
        className="w-36"
      />
      <Filter value={year} onChange={setYear} anyLabel="Year" options={YEARS.map(String)} className="w-24" />
    </div>
  )
}
